import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  Wallet,
  BarChart3,
  LogOut, 
  ChevronLeft, 
  ChevronRight,
  Menu,
  X
} from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import { AskAnalystButton } from './AskAnalyst/AskAnalystPanel';

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const { user, role, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const executeLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      icon: LayoutDashboard,
      label: 'Dashboard',
      path: '/',
      roles: ['admin', 'user']
    },
    {
      icon: Users,
      label: 'User Management',
      path: '/users',
      roles: ['admin']
    },
    {
      icon: Wallet,
      label: 'Quarterly Budget Planner',
      path: '/budget-planner',
      roles: ['admin', 'user']
    },
    // {
    //   icon: BarChart3,
    //   label: 'Retrospective Optimal Pacing',
    //   path: '/retrospective-optimal-pacing',
    //   roles: ['admin', 'user']
    // }
  ];

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(role)
  );

  const MenuItem = ({ icon: Icon, label, path, isActive }) => (
    <button
      onClick={() => {
        navigate(path);
        setIsMobileOpen(false);
      }}
      className={`
        w-full flex items-center rounded-xl text-left
        transition-all duration-200 font-medium
        ${isActive 
          ? 'bg-gradient-to-r from-mongodb-green-dark2 to-mongodb-green-dark1 text-white shadow-md' 
          : 'text-mongodb-gray-dark1 hover:bg-mongodb-green-light3 hover:text-mongodb-green-dark2'
        }
        ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-4 py-3'}
      `}
      title={isCollapsed ? label : ''}
    >
      <Icon className={`flex-shrink-0 ${isCollapsed ? 'w-6 h-6' : 'w-5 h-5'}`} />
      {!isCollapsed && <span className="text-left">{label}</span>}
    </button>
  );

  const SidebarContent = () => (
    <>
      {/* User Info */}
      <div className="p-4">
        <div className={`flex items-center gap-3 p-3 bg-gradient-to-br from-mongodb-green-light3 to-mongodb-green-light2 rounded-xl ${isCollapsed ? 'justify-center p-2' : ''}`}>
          <div className={`rounded-full bg-gradient-to-br from-mongodb-green-dark2 to-mongodb-green-dark1 flex items-center justify-center text-white font-bold flex-shrink-0 shadow-sm ${isCollapsed ? 'w-8 h-8' : 'w-10 h-10'}`}>
            {user ? user.charAt(0).toUpperCase() : 'U'}
          </div>
          {!isCollapsed && (
            <div className="overflow-hidden">
              <p className="text-mongodb-gray-dark3 font-semibold truncate text-sm">{user}</p>
              <p className="text-mongodb-green-dark2 text-xs capitalize font-medium">{role || 'Loading...'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 px-3 space-y-1">
        {!isCollapsed && filteredMenuItems.length > 0 && (
          <div className="px-2 py-1 text-xs font-semibold text-mongodb-gray-base uppercase tracking-wider">
            Menu
          </div>
        )}
        {filteredMenuItems.length === 0 && !isCollapsed ? (
          <div className="px-2 py-4 text-sm text-mongodb-gray-dark1 text-center">
            {role ? 'No menu items available' : 'Loading menu...'}
          </div>
        ) : (
          filteredMenuItems.map((item) => (
            <MenuItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              path={item.path}
              isActive={location.pathname === item.path}
            />
          ))
        )}
      </nav>

      {/* Logout Button */}
      <div className="p-3 border-t border-mongodb-gray-light2">
        <button
          onClick={handleLogout}
          className={`
            w-full flex items-center rounded-xl
            text-mongodb-red-base hover:bg-mongodb-red-light3 font-medium
            transition-all duration-200
            ${isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-4 py-3'}
          `}
          title={isCollapsed ? 'Logout' : ''}
        >
          <LogOut className={`flex-shrink-0 ${isCollapsed ? 'w-6 h-6' : 'w-5 h-5'}`} />
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>

      {/* Desktop Collapse Toggle */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="hidden md:flex absolute -right-3 top-32 w-8 h-8 bg-white rounded-full items-center justify-center text-mongodb-green-dark2 border-2 border-mongodb-green-dark1 hover:border-mongodb-green-dark2 hover:bg-mongodb-green-light3 hover:shadow-lg transition-all shadow-md z-20 group"
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? <ChevronRight className="w-5 h-5 group-hover:scale-110 transition-transform" /> : <ChevronLeft className="w-5 h-5 group-hover:scale-110 transition-transform" />}
      </button>
    </>
  );

  return (
    <>
      {/* Top Header Bar with Logos */}
      <header className="fixed top-0 left-0 right-0 h-20 bg-white border-b-2 border-mongodb-gray-light2 shadow-sm z-30">
        <div className="h-full px-6 flex items-center justify-between">
          {/* Left: Transmission Logo */}
          <div className="flex items-center gap-3">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className="md:hidden p-2 hover:bg-mongodb-gray-light3 rounded-lg transition-colors"
            >
              {isMobileOpen ? <X className="w-6 h-6 text-mongodb-gray-dark2" /> : <Menu className="w-6 h-6 text-mongodb-gray-dark2" />}
            </button>
            
            <div className="flex items-center p-2 rounded-lg hover:bg-mongodb-gray-light3 transition-colors">
              <img 
                src="/transmission-logo.svg" 
                alt="Transmission Logo" 
                className="h-10 w-auto transition-transform hover:scale-105 cursor-pointer"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>

          {/* Center: Title */}
          <div className="hidden sm:block flex-1 text-center">
            <p className="text-mongodb-gray-dark1 text-sm font-medium">
              Marketing Mix Modeling Dashboard - Powered by Transmission
            </p>
          </div>

          {/* Right: Ask Analyst */}
          <div className="flex items-center shrink-0">
            <AskAnalystButton />
          </div>
        </div>
      </header>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-30 z-40 mt-20"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Desktop Sidebar */}
      <aside
        className={`
          hidden md:flex flex-col
          fixed left-0 top-20 bottom-0
          bg-white border-r border-mongodb-gray-light2 shadow-sm
          transition-all duration-300 z-20
          ${isCollapsed ? 'w-16' : 'w-64'}
        `}
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Drawer */}
      <aside
        className={`
          md:hidden flex flex-col
          fixed left-0 top-20 bottom-0 w-64
          bg-white border-r border-mongodb-gray-light2 shadow-lg
          transition-transform duration-300 z-40
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <SidebarContent />
      </aside>

      {/* Logout Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={executeLogout}
        title="Logout"
        message="Are you sure you want to logout?"
        type="warning"
        confirmText="Logout"
      />
    </>
  );
};

export default Sidebar;

